// Toast notification context provider
// CREATED: Centralized toast management with queue system and auto-cleanup
// Provides showToast function accessible throughout the app via useToast hook
// UPDATED: Added 'warning' type with 8s default duration for propagation feedback

import { createContext, useState, useCallback, ReactNode } from 'react';
import { ToastContainer, ToastItem } from '../components/common/ToastContainer';
import type { ToastType } from '../components/common/Toast';

interface ToastContextType {
  showToast: (message: string, type?: ToastType, duration?: number) => void;
}

export const ToastContext = createContext<ToastContextType | undefined>(undefined);

const DEFAULT_DURATION = {
  success: 3000,
  error: 5000,
  info: 4000,
  warning: 8000
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const showToast = useCallback((
    message: string,
    type: ToastType = 'info',
    duration?: number
  ) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    const toastDuration = duration || DEFAULT_DURATION[type];

    const newToast: ToastItem = {
      id,
      message,
      type,
      duration: toastDuration
    };

    setToasts((prev) => [...prev, newToast]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
}
